/** @file Moves a socket's grip axis (a, b) and rewrites its socket bone's setup and length. */
import { z } from 'zod';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { throwError } from '@pose2d/format/throwError';
import { defineCommand } from '../defineCommand';
import { gripSetup } from './gripSetup';

/** socket.setPoints: moves a/b and re-derives the socket bone; the current points are a no-op (W2-31). */
export const command = defineCommand({
  name: 'socket.setPoints',
  description: 'Move a socket grip axis: a (base) and/or b (toward the tip) in its parent bone space; the socket bone setup and length follow (mounted items follow at compile time).',
  input: z.object({ target: z.string().min(1), a: prim.px.exactOptional(), b: prim.px.exactOptional() }).strict(),
  apply(draft, input, ctx) {
    const id = ctx.resolve('socket', input.target);
    const socket = draft.sockets[id] ?? throwError('NOT_FOUND', { kind: 'socket', ref: id });
    if (input.a === undefined && input.b === undefined) throwError('VALIDATION', undefined, { path: 'a', hint: 'give a, b or both' });
    if (JSON.stringify([input.a ?? socket.a, input.b ?? socket.b]) === JSON.stringify([socket.a, socket.b])) return;
    if (input.a) socket.a = input.a;
    if (input.b) socket.b = input.b;
    const { setup, length } = gripSetup(socket.a, socket.b);
    const bone = draft.bones[socket.bone];
    if (bone) Object.assign(bone, { setup, length });
    draft.meta.updatedAt = ctx.now;
  },
});
